/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useMemo } from 'react';
import { getCollection, getCollectionWithRefs } from 'src/firebase/firestoreUtils';
import { logInfoToConsole } from 'src/utils/loggingUtils';

/**
 * Hook to fetch Firestore collection with optional reference resolution
 * @param collectionName The name of the Firestore collection
 * @param options Optional settings for the hook
 * @param options.resolveRefs Array of field names that are references to resolve
 * @returns An object containing the data, loading state, and error message (if any)
 */
export const useFirestoreCollection = <T = any>(
    collectionName: string,
    options?: {
        resolveRefs?: string[];
    },
) => {
    const [data, setData] = useState<T[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Create a stable string key from resolveRefs for dependency tracking
    const resolveRefsKey = useMemo(() => options?.resolveRefs?.join(',') || '', [options?.resolveRefs]);

    useEffect(() => {
        // eslint-disable-next-line jsdoc/require-jsdoc
        const fetchData = async () => {
            try {
                setLoading(true);

                const result: T[] =
                    options?.resolveRefs && options.resolveRefs.length > 0
                        ? await getCollectionWithRefs<T>(collectionName, options.resolveRefs)
                        : await getCollection<T>(collectionName);

                setData(result);
                setError(null);
            } catch (err) {
                logInfoToConsole(`Error fetching ${collectionName}: ${err}`);
                setError(err instanceof Error ? err.message : 'Failed to fetch data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionName, resolveRefsKey]);

    return { data, loading, error };
};
